import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Upload, FileText, X as XIcon } from 'lucide-react';

import useAtsOffersApi, { OFFER_SIGNATURE_PROVIDERS } from './config';
import { PENDING_APPROVAL_MODE } from './offerPendingMode';
import api from '../../services/api';
import { getAtsSettings } from '../../services/atsSettingsService';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import A11yDialog from '../../components/ui/A11yDialog';
import DocumentViewerModal from '../../components/ui/DocumentViewerModal';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/ui/Button';
import OfferCreateDialog from '../ats-applications/OfferCreateDialog';
import OfferApprovalChainDrawer from './OfferApprovalChainDrawer';
import { toast } from 'sonner';

const Container = styled.div``;

// Gönder diyaloğunun KAPALI hâli. Bileşenin İÇİNDE tanımlıydı — her render'da yeni
// bir nesne üretiyordu; artık `closeSendDialog` bunu bir `useCallback` bağımlılığı
// olarak okuduğu için modül kapsamına alındı (sabit referans).
const CLOSED_SEND_DIALOG = {
  isOpen: false, id: null, provider: 'wet_ink_fallback',
  letterFileUrl: '', letterFileName: '', letterError: '', uploading: false,
};

// Süzgeç afişi — AtsRequisitionsPage'teki ikizinin birebir aynısı.
const FilterChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px 5px 12px;
  margin-right: 8px;
  border-radius: 14px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.primary.dark};
  background: ${({ theme }) => `${theme.colors.primary.main}1A`};
`;

const ChipClear = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  color: inherit;
`;

const Modal = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.5);
  display: ${(p) => (p.$open ? 'flex' : 'none')};
  align-items: center; justify-content: center; z-index: 1300; padding: 24px;

  /* FE-08 — telefonda 24px x 2 = 48px, 390px ekranin %12'si. */
  @media (max-width: 600px) {
    padding: 12px;
  }
`;
const ModalCard = styled.div`
  background: #fff; border-radius: 8px; max-width: 480px; width: 100%; padding: 24px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
  /* FE-08 — max-height YOKTU: perde "position: fixed; inset: 0" +
     "align-items: center" olduğundan, karttan uzun bir içerik ekranın ALTINDAN
     VE ÜSTÜNDEN taşıyordu ve hiçbir yolla kaydırılamıyordu — "Onayla"
     düğmesine telefonda hiç ulaşılamıyordu. "100%" perdenin iç kutusuna göre
     çözülür, yani perdenin iç boşluğu 24px'ten 12px'e inince kart otomatik
     olarak büyür (sabit 90vh bunu yapamaz).
     Bu, kabuğun "tek kaydırma" kuralını ÇİĞNEMEZ: diyalog ayrı bir katmandır,
     altındaki Main bu sırada zaten kilitlidir. */
  max-height: 100%;
  overflow-y: auto;
`;
const Field = styled.div` display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; font-size: 14px; `;
const FieldLabel = styled.label` color: #64748b; font-weight: 500; `;
const FieldSelect = styled.select` padding: 8px 12px; border: 1px solid #cbd5e0; border-radius: 4px; font-size: 14px; `;
const FieldText = styled.textarea` padding: 8px 12px; border: 1px solid #cbd5e0; border-radius: 4px; font-size: 14px; resize: vertical; min-height: 60px; font-family: inherit; `;
const Actions = styled.div` display: flex; gap: 8px; justify-content: flex-end; margin-top: 16px; `;
// #72 — yerel Primary/Secondary (kopyalanmis blok — onboarding-*/ats-*
// sayfalarinda ayni tarif) kaldirildi, paylasilan Button'a baglandi
// (bkz. t6-report.md).
const InfoNote = styled.p`
  background: #f0fdf4; border: 1px solid #bbf7d0; color: #166534;
  padding: 10px 12px; border-radius: 4px; font-size: 12.5px; margin: 0 0 14px;
`;
const LetterFileRow = styled.div`
  display: flex; align-items: center; gap: 10px;
  border: 1.5px dashed #D5DCE3; border-radius: 6px;
  padding: 10px 12px; background: #FAFBFC; margin-bottom: 6px;
`;
const LetterFileButton = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  background: #0291EF; color: #fff; border: none; border-radius: 4px;
  padding: 7px 10px; cursor: pointer; font-size: 12.5px;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
const LetterFileLabel = styled.span`
  color: #64748b; font-size: 12.5px; flex: 1; word-break: break-all;
  display: inline-flex; align-items: center; gap: 6px;
`;
const LetterError = styled.p`
  color: #E53935; font-size: 12.5px; margin: 4px 0 0;
`;
const RemoveBtnInline = styled.button`
  background: none; border: none; color: #64748b; cursor: pointer; padding: 4px;
  &:hover { color: #E53935; }
`;
const TemplateLink = styled.button`
  display: inline-flex; align-items: center; gap: 5px;
  background: none; border: none; padding: 0; cursor: pointer; font-family: inherit;
  color: #0291EF; font-size: 12.5px; text-decoration: underline; margin-bottom: 12px;
`;

// Durumun birincil adımı satırda görünür buton olarak sunulur (müşteri geri
// bildirimi: onay/ret yalnız ⋮ menüsündeyken bulunamıyordu). ⋮ menü ikincil
// aksiyonlar için aynen durur.
const RowActions = styled.div` display: flex; align-items: center; justify-content: flex-end; gap: 6px; `;
const RowBtn = styled.button`
  border: none; border-radius: 4px; padding: 5px 12px; font-size: 12px; font-weight: 600;
  cursor: pointer; white-space: nowrap; color: #fff; font-family: inherit;
  background: ${({ $tone, theme }) =>
    $tone === 'approve' ? '#10B981' : $tone === 'reject' ? '#E53935' : theme.colors.primary.main};
  &:hover:not(:disabled) { opacity: 0.88; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

// Durum başına aksiyonlar — backend geçişlerinin aynası.
//   `approverOnly: true` → aksiyon YALNIZ satırın atanmış onaycısına (ya da kiracı
//   yöneticisine) gösterilir; bayrağı SUNUCU hesaplar (`row.isApprover`), çünkü bir kişinin
//   iki `Users` satırı olabilir ve arayüz `user.id === approverUserId` karşılaştırmasını
//   güvenle yapamaz (kimlik ailesi).
//   `kind: 'revise'` → yeni sürüm açar (FE-33: `negotiating` durumuna arayüzden HİÇ
//   girilemiyordu, yalnız API'den).
//   `kind: 'rescind'` → işe alım geri alma (Ürün Kararı 2); gerekçe ZORUNLU.
const TRANSITIONS = {
  draft: [
    { action: 'submit-for-approval', labelKey: 'ats.offer.action.submitForApproval', iconType: 'submit_for_approval' },
    { action: 'revise', labelKey: 'ats.offer.action.revise', kind: 'revise', iconType: 'revise' },
    { action: 'withdraw', labelKey: 'ats.offer.action.withdraw', iconType: 'withdraw' },
  ],
  // Onaycının kararı İKİYE AYRILDI ve ikisi AYNI şey değildir:
  //   request-revision → "şunu düzelt de yeniden bakayım" (teklif TASLAĞA döner,
  //                      yeniden onaya çıkınca zincir BAŞTAN işler)
  //   reject-approval  → "bu teklif olmaz" (TERMİNAL; teklif ölür, koltuk serbest kalır,
  //                      üst kademe kararı EZEMEZ)
  // Gerekçe İKİSİNDE DE ZORUNLU — `requireReason` diyalogdaki butonu kilitler, sunucu da
  // boş gerekçede 400 döner.
  pending_approval: [
    { action: 'approve', labelKey: 'ats.offer.action.approve', iconType: 'approve', approverOnly: true },
    { action: 'request-revision', labelKey: 'ats.offer.action.requestRevision', kind: 'reject', requireReason: true, iconType: 'return_to_draft', approverOnly: true },
    { action: 'reject-approval', labelKey: 'ats.offer.action.rejectApproval', kind: 'reject', requireReason: true, iconType: 'reject', approverOnly: true },
    { action: 'revise', labelKey: 'ats.offer.action.revise', kind: 'revise', iconType: 'revise' },
    { action: 'withdraw', labelKey: 'ats.offer.action.withdraw', iconType: 'withdraw' },
  ],
  approved: [
    { action: 'send', labelKey: 'ats.offer.action.send', kind: 'send', iconType: 'send' },
    { action: 'revise', labelKey: 'ats.offer.action.revise', kind: 'revise', iconType: 'revise' },
    { action: 'withdraw', labelKey: 'ats.offer.action.withdraw', iconType: 'withdraw' },
  ],
  sent: [
    // Kabul artık KURUMSAL E-POSTA ister (Ürün Kararı 4): işe alınan kişinin şirket
    // adresini İK girer, adayın kişisel adresinden TÜRETİLMEZ. Bu yüzden düz onay
    // diyaloğu değil, kendi formu açılır.
    { action: 'accept', labelKey: 'ats.offer.action.accept', kind: 'accept', iconType: 'accept' },
    { action: 'reject', labelKey: 'ats.offer.action.reject', kind: 'reject', iconType: 'reject' },
    // Adaya yeni bağlantıyla yeniden e-posta: token yenilenir, eski bağlantı geçersizleşir.
    { action: 'resend', labelKey: 'ats.offer.action.resend', iconType: 'resend' },
    { action: 'revise', labelKey: 'ats.offer.action.revise', kind: 'revise', iconType: 'revise' },
    { action: 'expire', labelKey: 'ats.offer.action.expire', iconType: 'expire' },
    { action: 'withdraw', labelKey: 'ats.offer.action.withdraw', iconType: 'withdraw' },
  ],
  // `negotiating` canlı teklif DEĞİL: revizyon açılınca arşivlenen önceki sürümdür (backend
  // negotiating'i canlı saymaz; geçiş 409). Yalnız durum rozeti çizilir — aksiyon yok.
  negotiating: [],
  rejected: [],
  // Onaycının TERMİNAL reddi. Zincir öldü; hiçbir aksiyon yok (üst kademenin "yine de
  // onayla" diyebilmesi görev ayrılığını bozardı — sunucu da bu durumdan onaya geçirmez).
  approval_rejected: [],
  expired: [],
  withdrawn: [],
  // `accepted` ARTIK TERMİNAL DEĞİL: işe alım geri alınabilir (kadro yeniden açılır).
  accepted: [
    { action: 'rescind', labelKey: 'ats.offer.action.rescind', kind: 'rescind', iconType: 'reject', approverOnly: true },
  ],
  rescinded: [],
};

// Onay zinciri çekmecesinin açılabildiği durumlar (gerekçesi buildActions içinde).
const CHAIN_VISIBLE_STATUSES = ['draft', 'pending_approval', 'approved', 'approval_rejected'];

export default function AtsOffersPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const writable = canWrite ? canWrite('AT') : false;

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, id: null, action: '', label: '' });
  const [sendDialog, setSendDialog] = useState(CLOSED_SEND_DIALOG);
  const [rejectDialog, setRejectDialog] = useState({ isOpen: false, id: null, reason: '', requireReason: false });
  // Onay zinciri durumu (salt okunur): hangi adımdayız, kim onayladı, kim bekliyor.
  const [chainDrawer, setChainDrawer] = useState({ open: false, offerId: null });
  // Geri alma: gerekçe ZORUNLU — boşken buton kapalı, sunucu da 400 döner.
  const [rescindDialog, setRescindDialog] = useState({ isOpen: false, id: null, reason: '' });
  // Kabul: kurumsal e-posta ZORUNLU (Ürün Kararı 4). Boşken buton kapalı, sunucu da
  // `ats.hire.corporateEmailRequired` ile 400 döner.
  const [acceptDialog, setAcceptDialog] = useState({ isOpen: false, id: null, corporateEmail: '' });
  // Revizyon: yeni sürümün alanları mevcut satırdan ön doldurulur (revize = tekrar yazmak
  // değil, değişeni değiştirmek).
  const [reviseDialog, setReviseDialog] = useState(null);
  const [bandDialog, setBandDialog] = useState({ isOpen: false, id: null, note: '' });
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, id: null });
  const [busy, setBusy] = useState(false);
  // Teklif formu davranışı (AtsSettings): system = standart PDF otomatik,
  // custom = şirket kendi formunu yükler. İlk Send diyaloğunda bir kez çekilir.
  const [atsSettings, setAtsSettings] = useState(null);
  // Şablon dosyası özel sınıfa taşındı (StorageVisibility.Private — offerLetter amacı) — kayıtlı
  // adres artık doğrudan bir <a href> ile açılamaz. DocumentViewerModal kayıtlı adresi kendisi
  // `/v1/storage/view` üzerinden çözer.
  const [templateViewing, setTemplateViewing] = useState(false);

  const history = useHistory();
  const location = useLocation();

  // Onaylarım panelindeki "Teklif Onayları" kartının derin bağlantısı. Süzgeç
  // SUNUCUDA uygulanır (pendingForMe): yüklem kartın saydığı gövdenin AYNISIDIR
  // (AtsOfferDataAccess.PendingForApprover), yani kart 3 deyip liste 3 satır gösterir.
  const pendingOnly = useMemo(
    () => new URLSearchParams(location?.search || '').get('mode') === PENDING_APPROVAL_MODE,
    [location],
  );

  // parseTableParams her tablo değişiminde parametreleri SIFIRDAN kurar; kip bayrağı
  // bu yüzden state'e karıştırılmaz, türetilmiş katman olarak eklenir.
  const effectiveParams = useMemo(
    () => (pendingOnly ? { ...queryParams, pendingForMe: true } : queryParams),
    [queryParams, pendingOnly],
  );

  const clearPendingFilter = useCallback(() => {
    history.replace(location?.pathname || '/ats/offers');
  }, [history, location]);

  const { fetching, data, pagination, columns: baseColumns, action, remove, refresh } = useAtsOffersApi(effectiveParams);

  // FE-28 — satıra tıklayınca başvuru detayına git. Teklif listesinden adaya ulaşmanın
  // BAŞKA bir yolu yoktu: İK, teklifin hangi başvuruya ait olduğunu elle arıyordu.
  const handleRowClick = useCallback((row) => {
    if (row?.applicationId) history.push(`/ats/applications/${row.applicationId}`);
  }, [history]);

  const openSendDialog = useCallback((id) => {
    setSendDialog({ ...CLOSED_SEND_DIALOG, isOpen: true, id });
    if (!atsSettings) {
      getAtsSettings()
        .then((res) => setAtsSettings(res.data?.data || { offerLetterMode: 'system' }))
        .catch(() => setAtsSettings({ offerLetterMode: 'system' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atsSettings]);

  const handleLetterFile = useCallback(async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setSendDialog((prev) => ({ ...prev, uploading: true, letterError: '' }));
    try {
      const presignRes = await api.post(
        `/v1/storage/presign?fileName=${encodeURIComponent(file.name)}&mimeType=${encodeURIComponent(file.type || 'application/octet-stream')}&purpose=${encodeURIComponent('offerLetter')}`,
      );
      const { upload_url, file_url } = presignRes.data;
      await axios.put(upload_url, file, {
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });
      setSendDialog((prev) => ({ ...prev, uploading: false, letterFileUrl: file_url, letterFileName: file.name }));
    } catch {
      // FE-26 — yükleme çıplak `axios.put` ile yapılır (api.js interceptor'ı YOK, yani toast
      // da yok). Hata yutulunca buton "Yükle"ye dönüyor, hiçbir şey yazmıyor; kullanıcı
      // "Gönder"e basıp anlamadığı bir `letterFileRequired` hatası alıyordu.
      setSendDialog((prev) => ({ ...prev, uploading: false, letterError: t('ats.offer.uploadFailed') }));
    }
  }, [t]);

  const handleDataStateChange = useCallback((act, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => setFilterList(newFilterList), []);

  // Bir aksiyonun bu kullanıcıya gösterilip gösterilmeyeceği. `approverOnly` aksiyonlar
  // AT.Write'a DEĞİL, satırın atanmış onaycısı olmaya bakar: HR BP tipik olarak yalnız
  // AT.Read taşır (AtsAccessHelper ona Read verir) — eski kapı altında ASIL ONAYCI hiçbir
  // buton göremiyordu (FE-20). Bayrağı sunucu hesaplar; arayüz kimlik ailesini bilemez.
  const canDo = useCallback((row, tr) => (tr.approverOnly ? !!row?.isApprover : writable), [writable]);

  const openTransition = useCallback((row, tr) => {
    if (tr.kind === 'send') openSendDialog(row.id);
    else if (tr.kind === 'reject') setRejectDialog({ isOpen: true, id: row.id, reason: '', slug: tr.action, label: t(tr.labelKey), requireReason: !!tr.requireReason });
    else if (tr.kind === 'rescind') setRescindDialog({ isOpen: true, id: row.id, reason: '' });
    else if (tr.kind === 'accept') setAcceptDialog({ isOpen: true, id: row.id, corporateEmail: '' });
    else if (tr.kind === 'revise') setReviseDialog({ id: row.id, row });
    else setConfirmDialog({ isOpen: true, id: row.id, action: tr.action, label: t(tr.labelKey) });
  }, [t, openSendDialog]);

  const openChainDrawer = useCallback((row) => setChainDrawer({ open: true, offerId: row.id }), []);

  const buildActions = useCallback((row) => {
    if (!row) return [];
    // Arşiv sürümü (negotiating) ve geri alınmış satır SALT-OKUNURDUR: ⋮ menüsü hiç
    // çizilmez (Görev 15'te pinlenen sözleşme). Onay zinciri de bu satırlarda
    // açılmaz — okuma yüzeyi CANLI teklifin zinciridir.
    if (row.status === 'negotiating' || row.status === 'rescinded') return [];
    // Zincir durumu SALT OKUNURDUR ve yalnız onay kararının HÂLÂ bilgi taşıdığı
    // durumlarda açılır — brief'in dört sorusu (hangi adımdayız · kim onayladı · kim
    // bekliyor · red/revizyon gerekçesi) tam olarak bu dört durumda sorulur:
    //   draft              → "neden geri gönderildi?"
    //   pending_approval   → "kim bekliyor, kim onayladı?"
    //   approved           → "zinciri kim tamamladı?"
    //   approval_rejected  → "kim, neden reddetti?"
    // Gönderilmiş/kabul edilmiş satırda süreç ADAYA geçmiştir; oraya aksiyon eklemek
    // "onaycı olmayan kullanıcıda satırda aksiyon kalmaz" sözleşmesini bozardı.
    const actions = CHAIN_VISIBLE_STATUSES.includes(row.status)
      ? [{
        type: 'details',
        label: t('ats.offer.chain.title'),
        onClick: () => openChainDrawer(row),
      }]
      : [];
    const transitions = TRANSITIONS[row.status] || [];
    transitions.forEach((tr) => {
      if (!canDo(row, tr)) return;
      actions.push({
        // `|| 'edit'` YEDEĞİ KALDIRILDI: `iconType` beyan etmeyen her geçiş
        // kalem ikonuna düşüyordu, dolayısıyla taslak/onayda/gönderildi
        // durumlarında üç aksiyon yan yana ÜÇ AYNI kalemle çiziliyordu (ve
        // `key={action.type}` mükerrer oluyordu). Artık TRANSITIONS'taki her
        // geçiş `iconType` beyan eder; beyan etmeyen bir geçiş eklenirse
        // OptionsMenu geliştirme ortamında konsola uyarı basar.
        type: tr.iconType,
        label: t(tr.labelKey),
        onClick: () => openTransition(row, tr),
      });
    });
    // Bant istisnası yalnız onay bekleyen teklifte imzalanır (backend aksi hâlde 409
    // ats.offer.invalidTransition) — taslak/müzakere satırında aksiyon gösterilmez.
    // İmza da onay kapısına tabidir: yalnız atanmış onaycı.
    if (row.status === 'pending_approval' && row.isApprover
      && row.bandViolationApprovalRequired && row.bandExceptionApprovedByUserId == null) {
      actions.push({ type: 'band_exception', label: t('ats.offer.action.approveBandException'),
        onClick: () => setBandDialog({ isOpen: true, id: row.id, note: '' }) });
    }
    // Silme AT.Write'a bağlı kalır; kabul edilmiş ve geri alınmış satırlar denetim izidir.
    if (writable && row.status !== 'accepted') {
      if (actions.length > 0) actions.push({ type: 'divider' });
      actions.push({ type: 'delete', label: t('common.delete'),
        onClick: () => setDeleteDialog({ isOpen: true, id: row.id }) });
    }
    return actions;
  }, [t, writable, canDo, openTransition, openChainDrawer]);

  // Durum başına satırda gösterilen birincil butonlar
  const buildInlineButtons = useCallback((row) => {
    if (!row) return null;
    const buttons = [];

    if (row.status === 'draft' && writable) {
      buttons.push(
        <RowBtn key="submit" onClick={() =>
          setConfirmDialog({ isOpen: true, id: row.id, action: 'submit-for-approval', label: t('ats.offer.action.submitForApproval') })}>
          {t('ats.offer.action.submitForApproval')}
        </RowBtn>,
      );
    } else if (row.status === 'pending_approval' && row.isApprover) {
      const bandBlocked = row.bandViolationApprovalRequired && row.bandExceptionApprovedByUserId == null;
      // FE-19 — bant ihlalinde ESKİDEN devre dışı bir "Onayla" butonu ve HTML `title`
      // ipucu vardı: dokunmatik cihazda ipucu HİÇ görünmüyor, kullanıcı neden
      // onaylayamadığını anlamıyordu. Artık yapılacak iş doğrudan buton olarak duruyor.
      buttons.push(bandBlocked ? (
        <RowBtn key="band" data-role="band-exception-inline"
          onClick={() => setBandDialog({ isOpen: true, id: row.id, note: '' })}>
          {t('ats.offer.action.approveBandException')}
        </RowBtn>
      ) : (
        <RowBtn key="approve" $tone="approve"
          onClick={() =>
            setConfirmDialog({ isOpen: true, id: row.id, action: 'approve', label: t('ats.offer.action.approve') })}>
          {t('ats.offer.action.approve')}
        </RowBtn>
      ));
      // Onaycının İKİ AYRI kararı satırda da AYRI butonlardır: "Revizyon İste" teklifi
      // taslağa döndürür, "Reddet" öldürür. Tek butonun altına gizlemek, Faz 2'ye kadar
      // süren karışıklığın ta kendisiydi.
      buttons.push(
        <RowBtn key="revision" data-role="offer-request-revision" onClick={() =>
          setRejectDialog({
            isOpen: true, id: row.id, reason: '', slug: 'request-revision',
            label: t('ats.offer.action.requestRevision'), requireReason: true,
          })}>
          {t('ats.offer.action.requestRevision')}
        </RowBtn>,
      );
      buttons.push(
        <RowBtn key="reject" $tone="reject" data-role="offer-reject-approval" onClick={() =>
          setRejectDialog({
            isOpen: true, id: row.id, reason: '', slug: 'reject-approval',
            label: t('ats.offer.action.rejectApproval'), requireReason: true,
          })}>
          {t('ats.offer.action.rejectApproval')}
        </RowBtn>,
      );
    } else if (row.status === 'approved' && writable) {
      buttons.push(
        <RowBtn key="send" onClick={() => openSendDialog(row.id)}>
          {t('ats.offer.action.send')}
        </RowBtn>,
      );
    }

    return buttons.length > 0 ? buttons : null;
  }, [t, writable, openSendDialog]);

  // Aksiyon sütunu artık `writable`a değil, SATIRDA gerçekten bir aksiyon çıkıp çıkmadığına
  // bakar: yalnız AT.Read taşıyan atanmış onaycı da Onayla/Reddet görebilmeli (FE-20).
  // ESC, İptal düğmesi ve atma onayı AYNI kapatma yolunu kullanır (FE-25).
  const closeAcceptDialog = useCallback(() => setAcceptDialog({ isOpen: false, id: null, corporateEmail: '' }), []);
  const closeRejectDialog = useCallback(() => setRejectDialog({ isOpen: false, id: null, reason: '', slug: null, label: '', requireReason: false }), []);
  const closeRescindDialog = useCallback(() => setRescindDialog({ isOpen: false, id: null, reason: '' }), []);
  const closeBandDialog = useCallback(() => setBandDialog({ isOpen: false, id: null, note: '' }), []);
  const closeSendDialog = useCallback(() => setSendDialog(CLOSED_SEND_DIALOG), []);

  const columns = useMemo(() => {
    const anyActionable = data.some((row) => buildInlineButtons(row) || buildActions(row).length > 0);
    if (!anyActionable) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions', label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const inline = buildInlineButtons(row);
            const actions = buildActions(row);
            if (!inline && actions.length === 0) return null;
            return (
              // stopPropagation: aksiyon hücresine tıklamak satırın onRowClick'ini
              // (başvuru detayı) TETİKLEMEMELİ.
              <RowActions onClick={(e) => e.stopPropagation()}>
                {inline}
                {actions.length > 0 ? <OptionsMenu actions={actions} row={row} /> : null}
              </RowActions>
            );
          },
        },
      },
    ];
  }, [baseColumns, data, buildActions, buildInlineButtons]);

  return (
    <Container>
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.label}
        message={t('ats.offer.statusChangeConfirm', { action: confirmDialog.label })}
        onConfirm={async () => {
          setBusy(true);
          await action(confirmDialog.id, confirmDialog.action);
          setConfirmDialog({ isOpen: false, id: null, action: '', label: '' });
          setBusy(false);
        }}
        onCancel={() => setConfirmDialog({ isOpen: false, id: null, action: '', label: '' })}
        loading={busy}
      />

      {/* KABUL — işe alım anı. Kurumsal e-posta ZORUNLUDUR (Ürün Kararı 4): şifre-belirleme
          daveti bu adrese gider ve şirket postaları artık adayın kişisel adresine DÜŞMEZ.
          Aday zaten çalışansa sunucu 409 `ats.hire.internalCandidate` döner (iç transfer);
          o hata interceptor'dan toast olarak görünür ve diyalog AÇIK kalır. */}
      <A11yDialog
        open={acceptDialog.isOpen}
        onClose={closeAcceptDialog}
        dirty={!!acceptDialog.corporateEmail.trim()}
        overlay={Modal}
        card={ModalCard}
        overlayProps={{ $open: true }}
      >
        {({ titleId }) => (
          <>
            <h3 id={titleId} style={{ margin: '0 0 8px' }}>{t('ats.offer.action.accept')}</h3>
            <InfoNote>{t('ats.hire.acceptInfo')}</InfoNote>
            <Field>
              <FieldLabel>{t('ats.hire.corporateEmail')}</FieldLabel>
              <FieldText
                data-role="hire-corporate-email"
                type="email"
                placeholder={t('ats.hire.corporateEmailPlaceholder')}
                value={acceptDialog.corporateEmail}
                onChange={(e) => setAcceptDialog({ ...acceptDialog, corporateEmail: e.target.value })} />
            </Field>
            <Actions>
              <Button variant="outline" onClick={closeAcceptDialog}>
                {t('common.cancel')}
              </Button>
              <Button disabled={busy || !acceptDialog.corporateEmail.trim()} loading={busy} onClick={async () => {
                setBusy(true);
                const res = await action(acceptDialog.id, 'accept', {
                  corporateEmail: acceptDialog.corporateEmail.trim(),
                });
                setBusy(false);
                if (!res) return; // hata: diyalog açık kalsın, girilen adres kaybolmasın
                // EO-12: Employee açılmadıysa (çakışma / iç aday) işe alımcı bunu GÖRMELİ —
                // eskiden yalnız API log'unda bir satır vardı.
                if (res.employeeCreated === false) {
                  toast.warning(res.reason === 'internalCandidate'
                    ? t('ats.hire.internalCandidate')
                    : t('ats.offer.acceptedButEmployeeSkipped'));
                }
                closeAcceptDialog();
              }}>{t('common.confirm')}</Button>
            </Actions>
          </>
        )}
      </A11yDialog>

      {/* Gönder diyaloğu: imza sağlayıcı + teklif formu. Kabul token'ı işe alımcıya HİÇ
          gösterilmez — yalnız adayın e-postasındaki bağlantıda yaşar (EO-11). Başarıda diyalog
          kapanır (toast interceptor'dan); başarısızlıkta (ör. e-posta gönderilemedi → 502)
          açık kalır ki yüklenen form ve seçimler kaybolmadan yeniden denenebilsin. */}
      <A11yDialog
        open={sendDialog.isOpen}
        onClose={closeSendDialog}
        dirty={!!sendDialog.letterFileUrl}
        overlay={Modal}
        card={ModalCard}
        overlayProps={{ $open: true }}
      >
        {({ titleId }) => (
          <>
            <h3 id={titleId} style={{ margin: '0 0 16px' }}>{t('ats.offer.action.send')}</h3>
            <Field>
              <FieldLabel>{t('ats.offer.signatureProviderLabel')}</FieldLabel>
              <FieldSelect value={sendDialog.provider}
                onChange={(e) => setSendDialog({ ...sendDialog, provider: e.target.value })}>
                {OFFER_SIGNATURE_PROVIDERS.map((p) => (
                  <option key={p} value={p}>{t(`ats.offer.signatureProvider.${p}`)}</option>
                ))}
              </FieldSelect>
            </Field>

            {/* Teklif formu: tenant ayarına göre standart PDF bilgisi ya da
                şirketin kendi formunun (PDF/Word) zorunlu yüklemesi. */}
            {(atsSettings?.offerLetterMode || 'system') === 'custom' ? (
              <Field>
                <FieldLabel>{t('ats.offer.letterFileLabel')}</FieldLabel>
                {atsSettings?.offerLetterTemplateFileUrl && (
                  <TemplateLink
                    type="button"
                    data-testid="offer-letter-template-view-trigger"
                    onClick={() => setTemplateViewing(true)}
                  >
                    <FileText size={13} />
                    {t('ats.offer.downloadTemplate', {
                      name: atsSettings.offerLetterTemplateFileName || t('ats.settings.templateFile'),
                    })}
                  </TemplateLink>
                )}
                <LetterFileRow>
                  <LetterFileButton type="button" disabled={sendDialog.uploading}
                    onClick={() => document.getElementById('offer-letter-file-input')?.click()}>
                    <Upload size={13} />
                    {sendDialog.uploading ? '...' : t('ats.offer.uploadLetterFile')}
                  </LetterFileButton>
                  <LetterFileLabel>
                    {sendDialog.letterFileName || t('ats.offer.letterFileHint')}
                  </LetterFileLabel>
                  {sendDialog.letterFileUrl && (
                    <RemoveBtnInline type="button"
                      onClick={() => setSendDialog({ ...sendDialog, letterFileUrl: '', letterFileName: '' })}>
                      <XIcon size={15} />
                    </RemoveBtnInline>
                  )}
                  <input
                    id="offer-letter-file-input"
                    type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={handleLetterFile}
                    style={{ display: 'none' }}
                  />
                </LetterFileRow>
                {sendDialog.letterError && <LetterError>{sendDialog.letterError}</LetterError>}
              </Field>
            ) : (
              <InfoNote>{t('ats.offer.systemLetterInfo')}</InfoNote>
            )}

            <Actions>
              <Button variant="outline" onClick={closeSendDialog}>
                {t('common.cancel')}
              </Button>
              <Button disabled={busy || sendDialog.uploading} loading={busy} onClick={async () => {
                const customMode = (atsSettings?.offerLetterMode || 'system') === 'custom';
                if (customMode && !sendDialog.letterFileUrl) {
                  setSendDialog({ ...sendDialog, letterError: t('ats.offer.letterFileRequired') });
                  return;
                }
                setBusy(true);
                const res = await action(sendDialog.id, 'send', {
                  signatureProvider: sendDialog.provider,
                  offerLetterFileUrl: sendDialog.letterFileUrl || undefined,
                });
                setBusy(false);
                if (res) closeSendDialog();
              }}>{t('common.confirm')}</Button>
            </Actions>
          </>
        )}
      </A11yDialog>

      {/* Reject dialog */}
      <A11yDialog
        open={rejectDialog.isOpen}
        onClose={closeRejectDialog}
        dirty={!!(rejectDialog.reason || '').trim()}
        overlay={Modal}
        card={ModalCard}
        overlayProps={{ $open: true }}
      >
        {({ titleId }) => (
          <>
            <h3 id={titleId} style={{ margin: '0 0 16px' }}>{rejectDialog.label || t('ats.offer.action.reject')}</h3>
            {rejectDialog.slug === 'reject-approval' && (
              <InfoNote style={{ background: '#FEF2F2', borderColor: '#FECACA', color: '#B3261E' }}>
                {t('ats.offer.rejectApprovalInfo')}
              </InfoNote>
            )}
            {rejectDialog.slug === 'request-revision' && (
              <InfoNote style={{ background: '#FEFCE8', borderColor: '#FDE68A', color: '#854D0E' }}>
                {t('ats.offer.requestRevisionInfo')}
              </InfoNote>
            )}
            <Field>
              <FieldLabel>{t('ats.offer.rejectReason')}</FieldLabel>
              <FieldText data-role="offer-decision-reason" value={rejectDialog.reason}
                onChange={(e) => setRejectDialog({ ...rejectDialog, reason: e.target.value })} />
            </Field>
            <Actions>
              <Button variant="outline" onClick={closeRejectDialog}>{t('common.cancel')}</Button>
              {/* Gerekçe zorunluysa buton kilitli: sunucu da 400 döner, ama kullanıcı
                  hatayı toast'ta değil, formda görmeli. */}
              <Button
                disabled={busy || (rejectDialog.requireReason && !(rejectDialog.reason || '').trim())}
                loading={busy} onClick={async () => {
                setBusy(true);
                const res = await action(rejectDialog.id, rejectDialog.slug || 'reject', { reason: rejectDialog.reason || undefined });
                // FE-31 — diyalog ESKİDEN KOŞULSUZ kapanıyordu; sunucu isteği
                // reddedince (403 / durum çakışması) yazılan gerekçe siliniyordu.
                if (res) closeRejectDialog();
                setBusy(false);
              }}>{t('common.confirm')}</Button>
            </Actions>
          </>
        )}
      </A11yDialog>

      <OfferApprovalChainDrawer
        open={chainDrawer.open}
        offerId={chainDrawer.offerId}
        onClose={() => setChainDrawer({ open: false, offerId: null })}
      />

      {/* İşe alım geri alma — gerekçe ZORUNLU (Ürün Kararı 2). Sunucu kadroyu yeniden
          açar ve arşive kilitlenen ilanları `paused`a alır; ÇALIŞAN KAYDI SİLİNMEZ. */}
      <A11yDialog
        open={rescindDialog.isOpen}
        onClose={closeRescindDialog}
        dirty={!!rescindDialog.reason.trim()}
        overlay={Modal}
        card={ModalCard}
        overlayProps={{ $open: true }}
      >
        {({ titleId }) => (
          <>
            <h3 id={titleId} style={{ margin: '0 0 8px' }}>{t('ats.offer.action.rescind')}</h3>
            <InfoNote>{t('ats.offer.rescindInfo')}</InfoNote>
            <Field>
              <FieldLabel>{t('ats.offer.rescindReason')}</FieldLabel>
              <FieldText data-role="rescind-reason" value={rescindDialog.reason}
                onChange={(e) => setRescindDialog({ ...rescindDialog, reason: e.target.value })} />
            </Field>
            <Actions>
              <Button variant="outline" onClick={closeRescindDialog}>{t('common.cancel')}</Button>
              <Button disabled={busy || !rescindDialog.reason.trim()} loading={busy} onClick={async () => {
                setBusy(true);
                const res = await action(rescindDialog.id, 'rescind', { reason: rescindDialog.reason.trim() });
                setBusy(false);
                if (res) {
                  // Çalışan kaydı DURUYOR — İK'yı ayrılış akışına yönlendir.
                  if (res.employeeId) toast.info(t('ats.offer.rescindEmployeeRemains'));
                  closeRescindDialog();
                }
              }}>{t('common.confirm')}</Button>
            </Actions>
          </>
        )}
      </A11yDialog>

      {/* Revizyon — yeni sürüm açar (FE-33). Alanlar mevcut satırdan ön doldurulur;
          önceki sürüm arşive (`negotiating`) düşer ve geçmişte kalır. */}
      {reviseDialog && (
        <OfferCreateDialog
          open
          mode="revise"
          offerId={reviseDialog.id}
          initialOffer={reviseDialog.row}
          application={{
            id: reviseDialog.row?.applicationId,
            requisitionId: reviseDialog.row?.requisitionId,
            candidateFullName: reviseDialog.row?.candidateFullName,
            jobPostingTitle: reviseDialog.row?.jobPostingTitle,
          }}
          onClose={() => setReviseDialog(null)}
          onCreated={() => { setReviseDialog(null); refresh(); }}
        />
      )}

      {/* Band-exception approval dialog */}
      <A11yDialog
        open={bandDialog.isOpen}
        onClose={closeBandDialog}
        dirty={!!(bandDialog.note || '').trim()}
        overlay={Modal}
        card={ModalCard}
        overlayProps={{ $open: true }}
      >
        {({ titleId }) => (
          <>
            <h3 id={titleId} style={{ margin: '0 0 16px' }}>{t('ats.offer.action.approveBandException')}</h3>
            <Field>
              <FieldLabel>{t('ats.offer.bandExceptionNote')}</FieldLabel>
              <FieldText value={bandDialog.note}
                onChange={(e) => setBandDialog({ ...bandDialog, note: e.target.value })} />
            </Field>
            <Actions>
              <Button variant="outline" onClick={closeBandDialog}>{t('common.cancel')}</Button>
              <Button disabled={busy} loading={busy} onClick={async () => {
                setBusy(true);
                const res = await action(bandDialog.id, 'approve-band-exception', { note: bandDialog.note || undefined });
                // FE-31 — yalnız BAŞARIDA kapan; onay notu kaybolmasın.
                if (res) closeBandDialog();
                setBusy(false);
              }}>{t('common.confirm')}</Button>
            </Actions>
          </>
        )}
      </A11yDialog>

      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        title={t('common.delete')}
        message={t('ats.offer.deleteConfirm')}
        onConfirm={async () => { setBusy(true); await remove(deleteDialog.id); setDeleteDialog({ isOpen: false, id: null }); setBusy(false); }}
        onCancel={() => setDeleteDialog({ isOpen: false, id: null })}
        loading={busy}
      />

      {useMemo(() => (
        <DataTable
          title={t('ats.offer.pageTitle')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          onRowClick={handleRowClick}
          toolbarActions={pendingOnly ? (
            <FilterChip>
              {t('ats.offer.pendingForMeBanner')}
              <ChipClear type="button" onClick={clearPendingFilter} title={t('ats.offer.clearPendingFilter')}
                aria-label={t('ats.offer.clearPendingFilter')}>
                <XIcon size={13} />
              </ChipClear>
            </FilterChip>
          ) : null}
        />
      ), [
        t, fetching, columns, data, pagination,
        filterList, handleFilterConfirm, handleDataStateChange, handleRowClick,
        pendingOnly, clearPendingFilter,
      ])}

      <DocumentViewerModal
        open={templateViewing}
        fileUrl={atsSettings?.offerLetterTemplateFileUrl}
        title={atsSettings?.offerLetterTemplateFileName}
        onClose={() => setTemplateViewing(false)}
      />
    </Container>
  );
}
